/**
 * Client API pour les utilisateurs
 * Endpoint: /api/user/users
 * Service backend: sphere-user-service via sphere-gateway
 */

import { HttpClient } from '@sphere/shared/data-access/http'
import type {
  User,
  UserWithCapabilities,
  UserDetailWithCapabilities,
  CreateUserDto,
  UpdateUserDto,
  UpdateProfileDto,
  SyncFromKeycloakDto,
  QueryUsersDto,
  FindUsersByIdsDto,
} from '@sphere/user/domain'
import type { IOffsetPaginationResponse } from '@sphere/shared/domain'

const BASE_PATH = '/users'

export const usersClient = {
  // ============================================================================
  // CRUD Operations
  // ============================================================================

  /**
   * Liste les utilisateurs avec pagination et filtres
   * Backend filtre automatiquement selon le scope utilisateur
   */
  async list(
    params?: QueryUsersDto
  ): Promise<IOffsetPaginationResponse<UserWithCapabilities>> {
    const { data } = await HttpClient.get<
      IOffsetPaginationResponse<UserWithCapabilities>
    >(`${BASE_PATH}`, { params })
    return data
  },

  /**
   * Récupère plusieurs utilisateurs par leurs IDs
   */
  async findAllByIds(ids: string[]): Promise<UserWithCapabilities[]> {
    if (ids.length === 0) return []
    // Utiliser le DTO pour le body
    const body: FindUsersByIdsDto = { ids }
    const { data } = await HttpClient.post<UserWithCapabilities[]>(
      `${BASE_PATH}/all-by-ids`,
      body
    )
    return data
  },

  /**
   * Récupère un utilisateur par ID
   */
  async getById(id: string): Promise<UserDetailWithCapabilities> {
    const { data } = await HttpClient.get<UserDetailWithCapabilities>(
      `${BASE_PATH}/${id}`
    )
    return data
  },

  /**
   * Crée ou met à jour un utilisateur (upsert)
   * Nécessite: rôle admin
   */
  async upsert(dto: CreateUserDto): Promise<UserDetailWithCapabilities> {
    const { data } = await HttpClient.post<UserDetailWithCapabilities>(
      BASE_PATH,
      dto
    )
    return data
  },

  /**
   * Met à jour un utilisateur (admin)
   */
  async update(
    id: string,
    dto: UpdateUserDto
  ): Promise<UserDetailWithCapabilities> {
    const { data } = await HttpClient.patch<UserDetailWithCapabilities>(
      `${BASE_PATH}/${id}`,
      dto
    )
    return data
  },

  /**
   * Supprime (désactive) un utilisateur
   */
  async delete(id: string): Promise<UserDetailWithCapabilities> {
    const { data } = await HttpClient.delete<UserDetailWithCapabilities>(
      `${BASE_PATH}/${id}`
    )
    return data
  },

  // ============================================================================
  // Profile Operations (self)
  // ============================================================================

  /**
   * Récupère mon profil complet
   */
  async getMe(): Promise<UserDetailWithCapabilities> {
    const { data } = await HttpClient.get<UserDetailWithCapabilities>(
      `${BASE_PATH}/me`
    )
    return data
  },

  /**
   * Récupère mon profil (sans relations)
   */
  async getMyProfile(): Promise<User> {
    const { data } = await HttpClient.get<User>(`${BASE_PATH}/me/profile`)
    return data
  },

  /**
   * Met à jour mon profil
   */
  async updateMyProfile(
    dto: UpdateProfileDto
  ): Promise<UserDetailWithCapabilities> {
    const { data } = await HttpClient.patch<UserDetailWithCapabilities>(
      `${BASE_PATH}/me/profile`,
      dto
    )
    return data
  },

  /**
   * Synchronise mon cache Keycloak
   */
  async syncMe(dto: SyncFromKeycloakDto): Promise<User> {
    const { data } = await HttpClient.post<User>(`${BASE_PATH}/me/sync`, dto)
    return data
  },

  /**
   * Met à jour mon lastSeenAt
   */
  async updateLastSeen(): Promise<void> {
    await HttpClient.post(`${BASE_PATH}/me/seen`)
  },

  // ============================================================================
  // Profile Operations (by ID - admin)
  // ============================================================================

  /**
   * Récupère le profil d'un utilisateur
   */
  async getProfile(id: string): Promise<User> {
    const { data } = await HttpClient.get<User>(`${BASE_PATH}/${id}/profile`)
    return data
  },

  /**
   * Met à jour le profil d'un utilisateur (admin)
   */
  async updateProfile(
    id: string,
    dto: UpdateProfileDto
  ): Promise<UserDetailWithCapabilities> {
    const { data } = await HttpClient.patch<UserDetailWithCapabilities>(
      `${BASE_PATH}/${id}/profile`,
      dto
    )
    return data
  },

  // ============================================================================
  // Status Operations (admin)
  // ============================================================================

  /**
   * Suspend un utilisateur
   */
  async suspend(
    id: string,
    reason?: string
  ): Promise<UserDetailWithCapabilities> {
    const { data } = await HttpClient.post<UserDetailWithCapabilities>(
      `${BASE_PATH}/${id}/suspend`,
      { reason }
    )
    return data
  },

  /**
   * Réactive un utilisateur suspendu
   */
  async unsuspend(id: string): Promise<UserDetailWithCapabilities> {
    const { data } = await HttpClient.post<UserDetailWithCapabilities>(
      `${BASE_PATH}/${id}/unsuspend`
    )
    return data
  },

  /**
   * Anonymise un utilisateur (RGPD)
   */
  async anonymize(id: string): Promise<UserDetailWithCapabilities> {
    const { data } = await HttpClient.post<UserDetailWithCapabilities>(
      `${BASE_PATH}/${id}/anonymize`
    )
    return data
  },

  // ============================================================================
  // Relations / Parents
  // ============================================================================

  /**
   * Vérifie qu'un utilisateur existe par son username
   */
  async lookupByUsername(
    username: string,
    role: string
  ): Promise<{ id: string; username: string | null; displayName: string | null }> {
    const encoded = encodeURIComponent(username.trim())
    const { data } = await HttpClient.get<{
      id: string
      username: string | null
      displayName: string | null
    }>(`${BASE_PATH}/lookup/username/${encoded}?role=${role}`)
    return data
  },

  /**
   * Ajoute un parent/tuteur à un utilisateur par son username
   */
  async addGuardian(
    childUserId: string,
    username: string
  ): Promise<{ id: string; fromUserId: string; toUserId: string; type: string }> {
    const { data } = await HttpClient.post<
      { id: string; fromUserId: string; toUserId: string; type: string }
    >(`${BASE_PATH}/${childUserId}/guardians`, { username: username.trim() })
    return data
  },

  /**
   * Retire un parent/tuteur d'un utilisateur
   */
  async removeGuardian(
    childUserId: string,
    guardianId: string
  ): Promise<{ deleted: boolean }> {
    const { data } = await HttpClient.delete<{ deleted: boolean }>(
      `${BASE_PATH}/${childUserId}/guardians/${guardianId}`
    )
    return data
  },
}
